var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
function Image(_a) {
    var transform = _a.transform, metadata = _a.metadata, index = _a.index;
    var _b = useState(false), isLoaded = _b[0], setIsLoaded = _b[1];
    return (_jsx("img", { loading: "lazy", onLoad: function () {
            setIsLoaded(true);
        }, src: metadata.src, style: {
            height: metadata.height,
            width: metadata.width,
            marginTop: metadata.offsetY,
            marginLeft: metadata.offsetX,
            borderRadius: metadata.borderRadius || '16px',
            transform: transform ? "translateX(".concat(transform, "px)") : 'none',
        }, className: clsx('object-cover shrink-0 max-w-[none] transition-opacity duration-300 ease-in-out', isLoaded ? 'opacity-100' : 'opacity-0') }, "image-".concat(index)));
}
var calculateWidthOfAllImages = function (images, gap) {
    return images.reduce(function (acc, image) {
        return acc + Number(image.width);
    }, images.length * gap);
};
// Because the animation loop that glides the images is asynchronous, it will not be notified
// when the component unmounts. This flag is used to stop the animation from doing
// when the component is unmounted.
var animating = false;
// The glider works by translating a container of images leftward at a rate of
// glideSpeed pixels per frame. In order to not require a large number of images,
// we duplicate the images and shift the leading image to the back of the line once it has
// glided out of view, creating the illusion of an endless stream of images.
function ImageGlider(_a) {
    var _b = _a.images, images = _b === void 0 ? [] : _b, _c = _a.gap, gap = _c === void 0 ? 10 : _c, _d = _a.glideSpeed, glideSpeed = _d === void 0 ? 1 : _d, _e = _a.useGradient, useGradient = _e === void 0 ? true : _e, _f = _a.backgroundColor, backgroundColor = _f === void 0 ? 'bg-surface-white' : _f;
    // how far the image container has travelled
    var _g = useState(0), totalDistanceGlided = _g[0], setTotalDistanceGlided = _g[1];
    // For each image, there is a counter that tracks how many times it has been shifted
    // to the back of the line. The counter value multiplied by the width of all images (and
    // the gaps between them) is the distance to translate the image to shift it to the back
    // of the line.
    var _h = useState([]), shiftCounters = _h[0], setShiftCounters = _h[1];
    // the width of double all of the base images, plus the gaps between them
    var _j = useState(function () {
        return calculateWidthOfAllImages(images.concat(images), gap);
    }), widthOfAllImages = _j[0], setWidthOfAllImages = _j[1];
    // the animate() function uses refs to the props rather than the props themselves (or state),
    // because it executes inside of a loop of rAFs, which will not capture changes to the props
    // (even if animate() were a useCallback) because rAFs are asynchronous. rAFs ensure the
    // animation is as smooth as it can be by firing once per (and at the beginning of each) frame.
    // the index of the current leading image
    var currentLeadingImageIndexRef = useRef(0);
    // how far the current leading image has glided
    var leadingImageDistanceGlidedRef = useRef(0);
    // the images to use in the animation
    var imagesRef = useRef(images);
    // the horizontal gap between images
    var gapRef = useRef(gap);
    // if the images/gap change (such as when resizing between mobile and desktop),
    // we need to reset glider with the new images' widths
    var resetAnimation = useCallback(function () {
        var newWidthOfAllImages = calculateWidthOfAllImages(images.concat(images), gap);
        setWidthOfAllImages(newWidthOfAllImages);
        setTotalDistanceGlided(0);
        leadingImageDistanceGlidedRef.current = 0;
        currentLeadingImageIndexRef.current = 0;
        setShiftCounters([]);
    }, [images, gap]);
    // Animation works by first updating how far the container has travelled and how far
    // the current leading image has travelled (since it became the leading image). If
    // the leading image has glided out of view (i.e. its width + 1 gap width), we shift it to the
    // back of the line. Once we shift it, we update the number of times it has been shifted,
    // and we assign the next image in the rotation to be the new leading image.
    var animate = function () {
        if (animating) {
            var allImagesToUse_1 = imagesRef.current.concat(imagesRef.current);
            leadingImageDistanceGlidedRef.current = leadingImageDistanceGlidedRef.current - glideSpeed;
            setTotalDistanceGlided(function (prevDistance) {
                return prevDistance - glideSpeed;
            });
            if (Math.abs(leadingImageDistanceGlidedRef.current) >
                allImagesToUse_1[currentLeadingImageIndexRef.current].width + gapRef.current) {
                setShiftCounters(function (prevCounters) {
                    var newValue = (prevCounters[currentLeadingImageIndexRef.current] || 0) + 1;
                    prevCounters[currentLeadingImageIndexRef.current] = newValue;
                    currentLeadingImageIndexRef.current =
                        (currentLeadingImageIndexRef.current + 1) % allImagesToUse_1.length;
                    leadingImageDistanceGlidedRef.current = 0;
                    return prevCounters;
                });
            }
            requestAnimationFrame(animate);
        }
        else {
            resetAnimation();
        }
    };
    useEffect(function () {
        resetAnimation();
        imagesRef.current = images;
        if (images.length > 0 && !animating) {
            animating = true;
            animate();
        }
    }, [images]);
    useEffect(function () {
        gapRef.current = gap;
    }, [gap]);
    // When the component unmounts, we need to stop the animation
    useEffect(function () {
        return function () {
            animating = false;
        };
    }, []);
    // Optionally, add a soft, blurred edge to the left side of the glider so images don't just
    // cut off abruptly
    var afterClassName = useGradient
        ? "after:absolute after:top-0 after:left-0 after:bottom-0 after:right-0 after:w-full after:h-full"
        : '';
    var boxShadowClassName = '';
    switch (backgroundColor) {
        case 'bg-action-surface-primary':
            boxShadowClassName = 'after:shadow-[inset_44px_0px_20px_-5px_#feb430]';
            break;
        case 'bg-surface-white':
            boxShadowClassName = 'after:shadow-[inset_44px_0px_20px_-5px_#ffffff]';
            break;
        case 'bg-surface-beige':
        default:
            boxShadowClassName = 'after:shadow-[inset_24px_0px_10px_-5px_#f8eacd]';
            break;
    }
    return (_jsx("div", __assign({ className: clsx('relative flex justify-start items-center md:overflow-hidden gap-x-[10px]', afterClassName, boxShadowClassName) }, { children: _jsx("div", __assign({ style: {
                transform: "translateX(".concat(totalDistanceGlided, "px)"),
                columnGap: "".concat(gap, "px"), // dynamically constructing this with tailwind doesn't work
            }, className: clsx('flex justify-start items-center') }, { children: images.concat(images).map(function (image, index) {
                return (_jsx(Image, { transform: (shiftCounters[index] || 0) * widthOfAllImages, metadata: image, index: index }, "image-".concat(index)));
            }) })) })));
}
export default ImageGlider;
